import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLocalization } from "@faire/web--source/common/localization/useLocalization";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackLoggedOutRetailersDeepLinkBlogClick } from "@faire/web-api--source/events/loggedOutRetailers/click/deepLinkBlog";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import brandApplication from "@faire/web-api--source/routes/apply";
import brands from "@faire/web-api--source/routes/brands";
import React, { Suspense, useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { HeaderCategoriesButton } from "@faire/retailer/components/Header/desktop/HeaderCategoriesDropdown/HeaderCategoriesButton";
import { useNavigationCardQuery } from "@faire/retailer/components/Header/desktop/HeaderCategoriesDropdown/useNavigationCardQuery";
import { HeaderDesktopLogo } from "@faire/retailer/components/Header/desktop/HeaderDesktopLogo";
import { LoginSection } from "@faire/retailer/components/Header/desktop/LoginSection";
import { useHeaderDesktopContent } from "@faire/retailer/components/Header/desktop/useHeaderDesktopContent";
import { HeaderCart } from "@faire/retailer/components/Header/HeaderCart";
import { HeaderSearch } from "@faire/retailer/components/Header/HeaderSearch";
import { HeaderAnchor, HeaderLine, HeaderLink, MAX_HEADER_WIDTH } from "@faire/retailer/components/Header/styled";
import { LazyLanguageSelector } from "@faire/retailer-visitor-shared/components/LanguageSelector/LazyLanguageSelector";
import { MarketNavElements } from "@faire/retailer/features/FaireMarkets/Navigation/MarketNavElements";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useIsSemDiscoverPage } from "@faire/retailer-visitor-shared/lib/useIsSemDiscoverPage";
import { blogPage } from "@faire/retailer-visitor-shared/paths/index";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
export var HeaderDesktopContent = function() {
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useState = _sliced_to_array(useState(false), 2), shouldExpandSearchBar = _useState[0], setShouldExpandSearchBar = _useState[1];
    var isDesktopAndAbove = useViewport().isDesktopAndAbove;
    var isMarketLinkVisible = useHeaderDesktopContent().isMarketLinkVisible;
    var isSemSimplifiedLpEnabled = useIsSemDiscoverPage();
    // prefetch navigation card when navigation redesign is enabled
    useNavigationCardQuery(ClientPlatformType.CLIENT_DESKTOP);
    // This function reference must be stable for page scrolling
    var onSearchFocus = useCallback(function(element, isOnToggle) {
        if (isDesktopAndAbove && isOnToggle) {
            var _element_getBoundingClientRect;
            setShouldExpandSearchBar(!!(element && ((_element_getBoundingClientRect = element.getBoundingClientRect()) === null || _element_getBoundingClientRect === void 0 ? void 0 : _element_getBoundingClientRect.width) < 300));
        }
    }, [
        isDesktopAndAbove
    ]);
    // This function reference must be stable for page scrolling
    var onSearchBlur = useCallback(function() {
        setShouldExpandSearchBar(false);
    }, []);
    return /*#__PURE__*/ _jsxs(Wrapper, {
        justify: "center",
        align: "center",
        $enableRedesign: isLoggedInRetailer,
        children: [
            /*#__PURE__*/ _jsx(HeaderDesktopLogo, {
                omitStyling: isLoggedInRetailer
            }),
            isLoggedInRetailer ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: "2x"
                    }),
                    /*#__PURE__*/ _jsx(HeaderCategoriesButton, {}),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: "2x"
                    })
                ]
            }) : null,
            isDesktopAndAbove ? /*#__PURE__*/ _jsx(HeaderSearch, {
                onFocus: onSearchFocus,
                onBlur: onSearchBlur,
                inputId: "top-search",
                inputDataTestId: "searchBarInput"
            }) : null,
            /*#__PURE__*/ _jsx(Spacer, {
                width: "2x"
            }),
            !shouldExpandSearchBar && !isSemSimplifiedLpEnabled ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(LanguageSelectorSection, {}),
                    !isLoggedInRetailer ? /*#__PURE__*/ _jsx(BlogEntryLink, {}) : null,
                    canAddToCart() ? null : /*#__PURE__*/ _jsx(HeaderLink, {
                        to: getCurrentPath().pathname.includes(brands()) ? brandApplication() : brands(),
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "3mF4JS",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Sell on Faire"
                                }
                            ]
                        })
                    }),
                    isMarketLinkVisible ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(MarketNavElements, {}),
                            /*#__PURE__*/ _jsx(HeaderLine, {}),
                            /*#__PURE__*/ _jsx(Spacer, {
                                width: "1x"
                            })
                        ]
                    }) : null
                ]
            }) : null,
            /*#__PURE__*/ _jsx(LoginSection, {}),
            canAddToCart() ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(HeaderCart, {
                        dataTestId: "headerCartButton"
                    }),
                    isLoggedInRetailer ? null : /*#__PURE__*/ _jsx(Spacer, {
                        width: "3x"
                    })
                ]
            }) : null
        ]
    });
};
var LanguageSelectorSection = /*#__PURE__*/ React.memo(function() {
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var _useRetailerStore = useRetailerStore([
        "showLanguageSelector",
        "showSEOLanguageSelector",
        "availableLanguageSelectorLocales"
    ]), showLanguageSelector = _useRetailerStore.showLanguageSelector, showSEOLanguageSelector = _useRetailerStore.showSEOLanguageSelector, availableLanguageSelectorLocales = _useRetailerStore.availableLanguageSelectorLocales;
    var _useUserStore = useUserStore([
        "browserLevelLanguage",
        "browserLevelLocaleKey"
    ]), browserLevelLanguage = _useUserStore.browserLevelLanguage, browserLevelLocaleKey = _useUserStore.browserLevelLocaleKey;
    return !isLoggedInRetailer && (showLanguageSelector || showSEOLanguageSelector) ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Suspense, {
                fallback: null,
                children: /*#__PURE__*/ _jsx(LazyLanguageSelector, {
                    eventKey: "logged-out-top",
                    browserLanguage: browserLevelLanguage,
                    browserLocale: browserLevelLocaleKey,
                    availableLocales: availableLanguageSelectorLocales,
                    assignLocalizedUrl: true
                })
            }),
            /*#__PURE__*/ _jsx(HeaderLine, {})
        ]
    }) : null;
});
var BlogEntryLink = function() {
    var _useLocalization = useLocalization(), locale = _useLocalization.intl.locale;
    return /*#__PURE__*/ _jsx(HeaderAnchor, {
        href: "".concat(blogPage(locale), "/"),
        "data-test-id": "blogEntryButton",
        onClick: function() {
            return trackLoggedOutRetailersDeepLinkBlogClick("blog", "desktop");
        },
        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "oe71Qa",
            defaultMessage: [
                {
                    type: 0,
                    value: "Blog"
                }
            ]
        })
    });
};
var Wrapper = styled(Row).withConfig({
    componentId: "sc-560c5696-0"
})([
    "width:100%;",
    ""
], function(param) {
    var $enableRedesign = param.$enableRedesign;
    return $enableRedesign ? css([
        "max-width:",
        "px;margin:auto;",
        "{padding:",
        " ",
        " 0;}"
    ], MAX_HEADER_WIDTH, responsiveMediaQueries.desktopAndAbove, getSpacing("2x"), getSpacing("6x")) : "";
});
